// Печатающиеся фразы тизера-приглашения у лаунчера: список фраз печатается по
// букве, стирается и сменяется следующей — по кругу. Ритм тот же, что у строки
// на главной (setupTypeline в app/javascript/application.js), но живёт здесь:
// виджет — отдельный бандл в Shadow DOM со своим реестром таймеров.
//
// Фразы — настройка КОНКРЕТНОГО сотрудника (Assistants::WidgetSettings), а не
// наша прошивка: виджет стоит на ЧУЖИХ сайтах, и «не просит прибавку» уместно
// только у консультанта платформы. Список из одной фразы (или пустой) печать не
// включает — по умолчанию тизер остаётся статичным приглашением, как был.
//
// Чистый модуль без DOM — тестируется в изоляции (teaser.test.mjs), как
// delay.ts и receipt.ts.

// Ритм (мс). Цикл фразы: пусто → печатается по букве → висит целой полминуты →
// два раза моргает и гаснет → снова пусто полминуты → следующая фраза.
//
// Долгие паузы — намеренно: тизер висит на ЧУЖОМ сайте рядом с контентом, за
// которым посетитель пришёл. Строка, которая печатается и стирается каждые
// пару секунд, тянет взгляд непрерывно и мешает читать страницу; фраза раз в
// минуту читается как реплика, а не как баннер. Держать её на экране дольше,
// чем печатать, — тоже намеренно: набор нужен, чтобы заметить появление, а
// прочитать успевают за секунды.
const TYPE_MS = 55;
// Фраза на экране целиком.
const HOLD_MS = 30_000;
// Уход: два моргания и затухание — длительность keyframes widget-teaser-leave
// в styles.ts, значения обязаны совпадать (иначе строка сменится до конца
// анимации или пузырь повисит погасшим).
const LEAVE_MS = 1400;
// Пусто между фразами.
const GAP_MS = 30_000;
// Первая пауза пустоты — короткая: тизер только что всплыл, и пустой пузырь на
// полминуты читался бы как поломка, а не как пауза.
const START_MS = 700;
// Потолок числа фраз — зеркало серверного (WidgetSettings::MAX_PHRASES):
// конфиг приезжает уже очищенным, но виджет кешируется на чужих сайтах и
// обязан пережить любой ответ сервера, в том числе будущий.
const MAX_PHRASES = 20;
// Срок отказа от приглашения (крестик). Две недели: достаточно, чтобы не
// надоедать в одну сессию и в ближайшие заходы, но не «никогда» — вернувшийся
// через месяц гость приглашение должен увидеть.
const DISMISS_MS = 14 * 24 * 60 * 60 * 1000;

/** Фаза цикла. `idle` — пузырь пуст и погашен, `typing` — строка набирается,
 *  `holding` — фраза висит целиком, `leaving` — моргает и гаснет. */
export type TeaserPhase = 'idle' | 'typing' | 'holding' | 'leaving';

/** Шаг цикла: какая фраза, сколько её букв на экране, в какой фазе тизер и
 *  через сколько миллисекунд делать следующий шаг. */
export interface TypeStep {
  index: number;
  typed: number;
  phase: TeaserPhase;
  delay: number;
}

/** Фразы приглашения из конфига: только непустые строки и не больше потолка.
 *  Поля нет (старый сервер / старый кеш бандла), null, мусор вместо массива —
 *  пустой список, то есть прежний статичный тизер.
 *
 *  @param raw значение teaser_phrases из конфига виджета. */
export function teaserPhrases(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];

  return raw
    .filter((item): item is string => typeof item === 'string')
    .map((item) => item.trim())
    .filter((item) => item.length > 0)
    .slice(0, MAX_PHRASES);
}

/** Стартовый шаг: пустой погашенный пузырь. Первая фраза не выкладывается
 *  готовой — её набор посетитель должен увидеть, иначе появление тизера
 *  неотличимо от статичной плашки. */
export function firstTypeStep(): TypeStep {
  return { index: 0, typed: 0, phase: 'idle', delay: START_MS };
}

/** Следующий шаг цикла. Рисовать всегда `phrases[index].slice(0, typed)`, а
 *  фазу вешать на элемент — гашение и моргание рисует CSS, не эта функция.
 *
 *  idle → набираем первую букву; typing → следующую, пока фраза не набрана
 *  целиком; holding → уход (моргание + затухание); leaving → следующая фраза
 *  по кругу, снова с пустой паузы. */
export function nextTypeStep(step: TypeStep, phrases: string[]): TypeStep {
  const phrase = phrases[step.index] ?? '';

  if (step.phase === 'holding') {
    return { index: step.index, typed: phrase.length, phase: 'leaving', delay: LEAVE_MS };
  }
  if (step.phase === 'leaving') {
    return { index: (step.index + 1) % (phrases.length || 1), typed: 0, phase: 'idle', delay: GAP_MS };
  }

  const typed = step.typed + 1;
  if (typed >= phrase.length) {
    return { index: step.index, typed: phrase.length, phase: 'holding', delay: HOLD_MS };
  }
  return { index: step.index, typed, phase: 'typing', delay: TYPE_MS };
}

/** Самая длинная фраза списка — невидимым близнецом она держит высоту пузыря,
 *  пока строка набирается и стирается (иначе тизер прыгал бы на каждой букве). */
export function longestPhrase(phrases: string[]): string {
  return phrases.reduce((longest, phrase) => (phrase.length > longest.length ? phrase : longest), '');
}

/** Что делать с меткой отказа от приглашения.
 *
 *  `none` — метки нет, приглашение показываем; `active` — отказ в силе, молчим;
 *  `stale` — метку надо СТЕРЕТЬ и показать приглашение. */
export type TeaserDismissal = 'none' | 'active' | 'stale';

/** Действует ли отказ от приглашения — по метке из localStorage.
 *
 *  Вынесено из виджета чистой функцией, потому что здесь ровно та ошибка, из-за
 *  которой на телефоне владельца оставался один пульс иконки: метку читали как
 *  «свежий отказ» временем `Date.now()`, то есть возраст всегда выходил нулевым
 *  и двухнедельный срок не наступал НИКОГДА.
 *
 *  Метка `'1'` — из первого режима, когда отказ был бессрочным И ставился
 *  обычным тапом по приглашению (им открывают чат на телефоне). Доказательством
 *  отказа она не является: `stale`, стереть и приглашать снова.
 *
 *  @param raw значение метки (null — метки нет).
 *  @param now текущее время в мс (Date.now() у вызывающего — функция чистая).
 *  @return что делать: показывать, молчать или стереть метку и показывать. */
export function teaserDismissal(raw: string | null, now: number): TeaserDismissal {
  if (raw === null || raw === '') return 'none';
  if (raw === '1') return 'stale';

  const at = Number(raw);
  // Мусор вместо времени и метка из будущего дальше собственного срока (сбитые
  // часы гостя) — тоже мусор: иначе кривое время глушит приглашение навсегда.
  if (!Number.isFinite(at) || at <= 0 || at - now > DISMISS_MS) return 'stale';
  return now - at < DISMISS_MS ? 'active' : 'stale';
}
